/**
 * Hook for fetching correlation graph data for a risk.
 *
 * Queries the asset that owns the risk and follows CORRELATED_WITH relationships
 * to provider resources. Only enabled when the risk originates from the
 * correlation engine (source === 'correlate-batch').
 */

import { useGraphQuery } from '@/hooks/useGraphQuery';
import { Risk, TreeNode } from '@/types';

export interface CorrelationNode {
  /** The asset that owns the risk (e.g. the DNS record / IP) */
  asset: { key: string; name: string; dns: string; class?: string } | null;
  /** Provider resources linked via CORRELATED_WITH */
  providers: Array<{
    key: string;
    name: string;
    displayName?: string;
    provider?: string;
    resourceType?: string;
    correlationType?: string;
  }>;
}

/**
 * Fetches the graph structure for a correlation-engine risk:
 *
 *   [Provider] ──CORRELATED_WITH──▶ [Asset] ──HAS_VULNERABILITY──▶ [Risk]
 *
 * Two queries run in parallel:
 *
 * 1. **Asset query** — finds the asset by dns and follows CORRELATED_WITH.
 *    Works when the CORRELATED_WITH is on the same node as the risk.
 *
 * 2. **Name query** — finds assets by name (the IP) and follows
 *    CORRELATED_WITH.  Covers the case where the CORRELATED_WITH
 *    relationship is on the bare-IP asset node while the risk is on
 *    the hostname asset (dns ≠ name).
 *
 * Results are merged: the asset comes from query 1 (the hostname node)
 * and providers are collected from both queries, deduplicated by key.
 */
export function useCorrelationGraphData(risk: Risk | null) {
  const isCorrelationRisk = risk?.source === 'correlate-batch';

  // Query 1: find the asset by dns (the hostname node) + its CORRELATED_WITH
  const {
    data: dnsData,
    isLoading: dnsLoading,
    status: dnsStatus,
  } = useGraphQuery({
    tree: true,
    enabled: isCorrelationRisk && !!risk?.dns,
    query: {
      node: {
        labels: ['Asset'],
        filters: [
          {
            field: 'dns',
            operator: '=',
            value: risk?.dns || '',
          },
        ],
        relationships: [
          {
            label: 'CORRELATED_WITH',
            target: {
              labels: ['Asset', 'AWSResource', 'CloudResource'],
              label_operator: 'OR',
            },
            optional: true,
          },
        ],
      },
    },
  });

  // Query 2: find assets by name (the IP) + their CORRELATED_WITH.
  // The CORRELATED_WITH may live on a bare-IP node whose name = the IP
  // address while the risk is on the hostname node.
  const { data: nameData, isLoading: nameLoading } = useGraphQuery({
    tree: true,
    enabled: isCorrelationRisk && !!risk?.dns,
    query: {
      node: {
        labels: ['Asset'],
        filters: [
          {
            field: 'dns',
            operator: '=',
            value: risk?.dns || '',
          },
        ],
        relationships: [
          {
            label: 'DISCOVERED',
            target: {
              labels: ['Asset'],
              relationships: [
                {
                  label: 'CORRELATED_WITH',
                  target: {
                    labels: ['Asset', 'AWSResource', 'CloudResource'],
                    label_operator: 'OR',
                  },
                  optional: true,
                },
              ],
            },
            optional: true,
          },
        ],
      },
    },
  });

  const parsed = mergeCorrelationData(
    parseCorrelationData(dnsData as TreeNode[] | undefined),
    parseNestedCorrelationData(nameData as TreeNode[] | undefined)
  );

  return {
    correlationData: parsed,
    isLoading: dnsLoading || nameLoading,
    isCorrelationRisk: Boolean(isCorrelationRisk),
    status: dnsStatus,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract provider entries from a relationship's target result. */
function extractProviders(rel: {
  label?: string;
  target?: { result?: import('@/types').GraphQueryResponse };
}): CorrelationNode['providers'] {
  const result = rel?.target?.result;
  const resources = [
    ...(result?.assets ?? []),
    ...(result?.awsresources ?? []),
    ...(result?.generics ?? []),
  ];
  return resources.map(resource => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const r = resource as Record<string, any>;
    const dns = r.dns as string | undefined;
    return {
      key: resource.key,
      name: resource.name ?? resource.key,
      displayName: (r.displayName as string | undefined) ?? dns,
      provider: (r.provider as string | undefined) ?? dns,
      resourceType: r.resourceType as string | undefined,
      correlationType: rel.label as string | undefined,
    };
  });
}

/** Parse query 1: Asset (hostname) → CORRELATED_WITH → Provider */
function parseCorrelationData(
  treeData: TreeNode[] | undefined
): CorrelationNode {
  if (!treeData?.length) return { asset: null, providers: [] };

  const node = treeData[0]?.node;
  if (!node) return { asset: null, providers: [] };

  const asset = node.result?.assets?.[0] ?? null;
  const providers = (node.relationships ?? []).flatMap(extractProviders);

  return {
    asset: asset
      ? { key: asset.key, name: asset.name, dns: asset.dns, class: asset.class }
      : null,
    providers,
  };
}

/** Parse query 2: Asset (hostname) → DISCOVERED → Asset (IP) → CORRELATED_WITH → Provider */
function parseNestedCorrelationData(
  treeData: TreeNode[] | undefined
): CorrelationNode {
  if (!treeData?.length) return { asset: null, providers: [] };

  const providers = treeData.flatMap(treeNode =>
    (treeNode.node?.relationships ?? []).flatMap(discoveredRel =>
      (discoveredRel?.target?.relationships ?? []).flatMap(extractProviders)
    )
  );

  return { asset: null, providers };
}

/** Merge results from both queries, deduplicating providers by key. */
function mergeCorrelationData(
  dns: CorrelationNode,
  nested: CorrelationNode
): CorrelationNode {
  const seen = new Set<string>();
  const providers = [...dns.providers, ...nested.providers].filter(p => {
    if (seen.has(p.key)) return false;
    seen.add(p.key);
    return true;
  });

  return { asset: dns.asset ?? nested.asset, providers };
}
